<template>
  <div class="aoe-kit-selection">
    <div class="mini-header">{{ t('kit.miniHeader') }}</div>
    <div class="kit-header">{{ t('kit.kitHeader') }}</div>
    <div v-if="loading" class="loading-state">
      {{ t('labels.AutoCompleteLoading') }}
    </div>
    <div v-else-if="error" class="error-state">
      {{ error }}
    </div>
   <div v-else class="kits-container">
      <div v-for="product in products" :key="product.productCode" class="kit-card mx-2">
        <div class="kit-image">
          <img
            v-if="product.attributes?.ProductPrimaryImage"
            :src="
              GetFullProductImageUrlFromStringDictionary(
                product.productCode,
              )
            "
            :alt="product.productName"
          />
        </div>
        <div class="kit-details">
          <h3 class="kit-name">{{ product.productName }}</h3>
          <div class="kit-sku" v-if="product.skus && product.skus.length > 0">
            <div class="sku-price">
              {{ formatPrice(product.skus[0].retailPrice) }}
              <span class="total-value" v-if="product.attributes?.TotalValue">
                {{ t('kit.totalValue', [product.attributes.TotalValue]) }}
              </span>
            </div>
            <div class="sku-stock" :class="getStockClass(product.skus[0].inventoryLevel)">
              {{ getInventoryLevelText(product.skus[0].inventoryLevel.inventoryLevel) }}
            </div>
          </div>

          <div class="details-section">
            <div
              class="details-header"
              @click="toggleDetails(product.productCode)"
              :class="{ 'details-open': expandedDetails[product.productCode] }"
            >
              <span>{{ t('kit.details') }}</span>
              <span class="details-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </span>
            </div>

            <div v-if="expandedDetails[product.productCode]" class="details-content">
              <div class="kit-components" v-if="product.skus && product.skus.length > 0 && kitComponents[product.skus[0].skuCode] && kitComponents[product.skus[0].skuCode].length > 0">
                <h4 class="components-title">{{ t('kit.productsInKit') }}</h4>
                <ul class="components-list">
                  <li v-for="component in kitComponents[product.skus[0].skuCode]" :key="component.skuCodeChild" class="component-item">
                    <div class="component-name">{{ component.details?.skuName || component.skuCodeChild }}</div>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div class="kit-actions">
            <AoeAddToCartButton
              :skucode="product.skus[0].skuCode"
              :availablequantity="product.skus[0].inventoryLevel.availableQty.toString()"
              :ordertypeidoverride="props.kitordertypeid"
              :kitcategorycode="props.kitcategorycode"
              displayquantityspinner="false"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="free-kit">
      {{ t('kit.startForFree') }}
      <a :href="`mailto:${customerServiceEmail}`">{{ customerServiceEmail }}</a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { ProductByCategoryAndOrderTypeGetList } from '@/api/Category'
import AoeAddToCartButton from '@/components/AoeAddToCartButton.vue'
import { FilterReinstatementKits } from '@/services/CustomerService'
import {
  GetFullProductImageDictionary,
  GetSkuComponentsWithDetails
} from '../services/SkuService.ts'
import { FormatCurrency } from '../services/currencyFormatter'

const props = defineProps<{
  kitordertypeid: string
  kitcategorycode: string
}>()

const productImageUrls = ref<Record<string, string>>({})
const loading = ref(true)
const error = ref('')
const products = ref<any[]>([])
const kitComponents = ref<Record<string, any[]>>({})
const expandedDetails = ref<Record<string, boolean>>({})
const { t, locale } = useI18n()

const customerServiceEmail = computed(() => {
  const currentLocale = locale.value.toLowerCase()
  if (currentLocale === 'fr' || currentLocale === 'fr-fr') {
    return 'info@pamperedchef.fr'
  }
  return 'info@pamperedchef.de'
})

const formatPrice = (price: number): string => {
  return FormatCurrency(price, null) || ''
}

const getInventoryLevelText = (inventoryLevel: string): string => {
  const levelMap: Record<string, string> = {
    'Available': t('partyOrderEntry.Available'),
    'LowStock': t('partyOrderEntry.LowStock'),
    'OutOfStock': t('partyOrderEntry.OutOfStock'),
    'StopSell': t('partyOrderEntry.StopSell')
  }
  return levelMap[inventoryLevel] || inventoryLevel
}

const getStockClass = (inventoryLevel: { inventoryLevel: string }) => {
  const level = inventoryLevel.inventoryLevel.toLowerCase()
  return {
    'stock-available': level === 'available',
    'stock-low': level === 'low',
    'stock-out': level === 'out of stock'
  }
}

const fetchKitComponents = async (skuCode: string) => {
  try {
    const componentsWithDetails = await GetSkuComponentsWithDetails(skuCode)

    if (componentsWithDetails && componentsWithDetails.length > 0) {
      // Format the components for display
      kitComponents.value[skuCode] = componentsWithDetails.map(item => ({
        skuCodeChild: item.component.skuCodeChild,
        quantity: item.component.quantity,
        details: item.details
      }))
    }
  } catch (err) {
    console.error(`Failed to fetch components for SKU ${skuCode}:`, err)
  }
}

const fetchKits = async () => {
  try {
    loading.value = true
    error.value = ''
    console.log(props.kitordertypeid, props.kitcategorycode)

    const response = await ProductByCategoryAndOrderTypeGetList({
      orderTypeId: props.kitordertypeid,
      categoryCode: props.kitcategorycode,
      includeInventoryLevels: true,
      beeNumber: null
    })

    if (response?.item?.products) {
      // Hide reinstatement kits unless the logged-in user is eligible
      const fetchedProducts = await FilterReinstatementKits(response.item.products)

      products.value = fetchedProducts
      const uniqueProductCodes = fetchedProducts.map(p => p.productCode)
      productImageUrls.value =
      await GetFullProductImageDictionary(uniqueProductCodes)

      // For each product's SKU, fetch its components
      const componentPromises = fetchedProducts
        .filter(product => product.skus && product.skus.length > 0)
        .map(product => fetchKitComponents(product.skus[0].skuCode))

      // Wait for all component fetches to complete
      await Promise.all(componentPromises)
    } else {
      products.value = []
    }
  } catch (err: any) {
    error.value = 'Failed to load kits: ' + (err.message || 'Unknown error')
  } finally {
    loading.value = false
  }
}

function GetFullProductImageUrlFromStringDictionary(productCode: string) {

  const productImageUrl = productImageUrls.value[productCode]

  if (productImageUrl) return productImageUrl
  return '#'
}

const toggleDetails = (productCode: string) => {
  if (expandedDetails.value[productCode]) {
    expandedDetails.value[productCode] = false
  } else {
    expandedDetails.value[productCode] = true
  }
}

onMounted(() => {
  fetchKits()
})
</script>

<style lang="scss" scoped>
.aoe-kit-selection {
  padding: $card-padding;
  text-align: center;

  .component-name {
    text-align: left;
    margin-left: $spacing-2;
  }
}

.loading-state,
.error-state {
  text-align: center;
  padding: 2rem;
  color: #666;
}

.error-state {
  color: #dc3545;
}

.kits-container {
display: flex ;
justify-content: center;
}

.kit-card {
  border: 1px solid #e0e0e0;
  border-radius: $radius-md;
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s;
}

.kit-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(black, 0.1);
}

.kit-image {
  height: 400px;
  overflow: hidden;
  background: var(--vt-c-white-soft);
}

.kit-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.kit-details {
  padding: $spacing-4;
}

.kit-name {
  @include typography-body-large;
  @include font-weight-medium;
  margin: 0 0 10px 0;
}

.kit-sku {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.sku-price {
  @include typography-h4;
  @include font-weight-semibold;
  color: #2c3e50;
}

.total-value {
  @include typography-body;
  @include font-weight-regular;
  color: #666;
  margin-left: 5px;
}

.sku-stock {
  @include typography-body;
  padding: $spacing-1 $spacing-2;
  border-radius: $radius-sm;
}
.mini-header {
  @include typography-h4;

  margin-bottom: 10px;
}
.kit-header {
  @include typography-h3;
  @include font-weight-bold;
  margin-bottom: 20px;
}

.details-section {
  border: 1px solid #e0e0e0;
  border-radius: $radius-sm;
  margin-bottom: 15px;
}

.details-header {
  display: flex;
  justify-content: space-between;
  padding: $spacing-2-5 $spacing-4;
  @include font-weight-medium;
  background-color: var(--vt-c-white-soft);
  cursor: pointer;
}

.details-header:hover {
  background-color: #e9ecef;
}

.details-icon {
  transition: transform 0.2s;
}

.details-open .details-icon {
  transform: rotate(180deg);
}

.details-content {
  padding: $spacing-4;
  border-top: 1px solid #e0e0e0;
}

.components-title {
  @include typography-body;
  @include font-weight-medium;
  margin: $spacing-2-5 0;
}

.components-list {
  list-style-type: disc;
  padding-left: 20px;
  margin: 0 0 15px 0;
}

.component-item {
  @include typography-body;
  padding: $spacing-1 0;
}

.kit-components {
  margin-top: 0;
  border-top: none;
  padding-top: 0;
}

.kit-actions {
  margin-top: 15px;
  text-align: center;
}

.kit-actions :deep(.btn) {
  background-color: #2c3e50;
  color: white;
  padding: $spacing-2 $spacing-4;
  border-radius: $radius-sm;
  transition: background-color 0.2s;
}

.kit-actions :deep(.btn:hover) {
  background-color: #34495e;
}

.kit-actions :deep(.btn:disabled) {
  background-color: #95a5a6;
  cursor: not-allowed;
}

.stock-available {
  background-color: #d4edda;
  color: #155724;
}

.stock-low {
  background-color: #fff3cd;
  color: #856404;
}

.stock-out {
  background-color: #f8d7da;
  color: #721c24;
}

@media (max-width: 768px) {
  .kits-container {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }

  .kit-image {
    height: 150px;
  }
}
</style>
